import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import ClearIcon from "@mui/icons-material/Clear";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import DevicesOutlinedIcon from "@mui/icons-material/DevicesOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";

import { DataGrid } from "@mui/x-data-grid";

import FactoryStockService from "../../services/FactoryStockService";


/* ============================================================
   CONSTANTS
============================================================ */

const DEFAULT_PAGE_SIZE = 50;

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];


/* ============================================================
   COMPONENT
============================================================ */

const StockDeviceFeatureList = () => {
  /* ==========================================================
     FILTER OPTIONS
  ========================================================== */

  const [deviceFeatures, setDeviceFeatures] = useState([]);

  const [manufacturers, setManufacturers] = useState([]);

  const [optionsLoading, setOptionsLoading] = useState(false);


  /* ==========================================================
     FILTER FORM STATE

     These values represent what the user is currently
     selecting/typing in the filter section.
  ========================================================== */

  const [featureFilter, setFeatureFilter] = useState("");

  const [manufacturerFilter, setManufacturerFilter] =
    useState("");

  const [searchInput, setSearchInput] = useState("");


  /* ==========================================================
     APPLIED FILTER STATE

     Only these values are sent to the API.

     This prevents API calls on every key press.
  ========================================================== */

  const [appliedFilters, setAppliedFilters] = useState({
    device_feature_id: "",
    manufacturer_id: "",
    search: "",
  });


  /* ==========================================================
     TABLE STATE
  ========================================================== */

  const [rows, setRows] = useState([]);

  const [totalRows, setTotalRows] = useState(0);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const [tableLoading, setTableLoading] = useState(false);


  /* ==========================================================
     REQUEST CONTROL

     Used to prevent an older API response from replacing
     newer table data.
  ========================================================== */

  const requestIdRef = useRef(0);


  /* ==========================================================
     SNACKBAR
  ========================================================== */

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });


  /* ==========================================================
     SHOW SNACKBAR
  ========================================================== */

  const showSnackbar = useCallback(
    (message, severity = "error") => {
      setSnackbar({
        open: true,
        message,
        severity,
      });
    },
    []
  );


  /* ==========================================================
     CLOSE SNACKBAR
  ========================================================== */

  const closeSnackbar = (_, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setSnackbar((previous) => ({
      ...previous,
      open: false,
    }));
  };


  /* ==========================================================
     LOAD FILTER OPTIONS
     
     POST
     /api/stock/device-feature/filter-options/

     Request:
     {}

     Response:
     {
       device_features: [],
       manufacturers: []
     }
  ========================================================== */

  const loadFilterOptions = useCallback(async () => {
    try {
      setOptionsLoading(true);

      const response =
        await FactoryStockService.deviceFeatureFilterOptions(
          {}
        );

      const data = response?.data || {};

      setDeviceFeatures(
        Array.isArray(data.device_features)
          ? data.device_features
          : []
      );

      setManufacturers(
        Array.isArray(data.manufacturers)
          ? data.manufacturers
          : []
      );
    } catch (error) {
      console.error(
        "StockDeviceFeatureList - filter options error:",
        error
      );

      showSnackbar(
        error?.response?.data?.message ||
          error?.response?.data?.detail ||
          "Unable to load device filter options.",
        "error"
      );
    } finally {
      setOptionsLoading(false);
    }
  }, [showSnackbar]);


  /* ==========================================================
     BUILD API FILTER OBJECT
  ========================================================== */

  const buildFilters = useCallback(() => {
    const filters = {};

    /*
     * Device Feature
     */

    if (appliedFilters.device_feature_id !== "") {
      filters.device_feature_id = Number(
        appliedFilters.device_feature_id
      );
    }

    /*
     * Manufacturer
     */

    if (appliedFilters.manufacturer_id !== "") {
      filters.manufacturer_id = Number(
        appliedFilters.manufacturer_id
      );
    }

    return filters;
  }, [appliedFilters]);


  /* ==========================================================
     LOAD DEVICE LIST
     
     POST
     /api/stock/factory/list/

     Example:
     {
       "filters": {
         "manufacturer_id": 8,
         "device_feature_id": 5
       },
       "search": "8661",
       "page": 1,
       "page_size": 50
     }
  ========================================================== */

  const loadDevices = useCallback(async () => {
    const currentRequestId =
      ++requestIdRef.current;

    try {
      setTableLoading(true);

      const requestBody = {
        filters: buildFilters(),

        search: appliedFilters.search.trim(),

        page: paginationModel.page + 1,

        page_size: Math.min(
          paginationModel.pageSize,
          200
        ),
      };

      console.log(
        "StockDeviceFeatureList Request:",
        requestBody
      );

      const response =
        await FactoryStockService.factoryStockList(
          requestBody
        );

      /*
       * Ignore stale API response.
       */

      if (
        currentRequestId !==
        requestIdRef.current
      ) {
        return;
      }

      const data = response?.data || {};

      const results = Array.isArray(
        data.results
      )
        ? data.results
        : [];


      /*
       * Format rows for DataGrid.
       */

      const formattedRows = results.map(
        (device, index) => ({
          ...device,

          id:
            device.id ??
            device.imei ??
            `device-${index}`,
        })
      );

      setRows(formattedRows);

      setTotalRows(
        Number(data.total || 0)
      );
    } catch (error) {
      console.error(
        "StockDeviceFeatureList - device list error:",
        error
      );

      if (
        currentRequestId !==
        requestIdRef.current
      ) {
        return;
      }

      setRows([]);

      setTotalRows(0);

      showSnackbar(
        error?.response?.data?.message ||
          error?.response?.data?.detail ||
          "Unable to load device inventory.",
        "error"
      );
    } finally {
      if (
        currentRequestId ===
        requestIdRef.current
      ) {
        setTableLoading(false);
      }
    }
  }, [
    appliedFilters,
    buildFilters,
    paginationModel.page,
    paginationModel.pageSize,
    showSnackbar,
  ]);


  /* ==========================================================
     INITIAL LOAD
  ========================================================== */

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);


  /* ==========================================================
     LOAD DEVICE LIST

     Runs when:
     - Applied filters change
     - Page changes
     - Page size changes
  ========================================================== */

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);


  /* ==========================================================
     APPLY FILTERS
  ========================================================== */

  const handleApplyFilters = () => {
    setPaginationModel((previous) => ({
      ...previous,
      page: 0,
    }));

    setAppliedFilters({
      device_feature_id: featureFilter,

      manufacturer_id:
        manufacturerFilter,

      search: searchInput.trim(),
    });
  };


  /* ==========================================================
     SEARCH ENTER KEY
  ========================================================== */

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();

      handleApplyFilters();
    }
  };


  /* ==========================================================
     CLEAR ALL FILTERS
  ========================================================== */

  const handleClearFilters = () => {
    setFeatureFilter("");

    setManufacturerFilter("");

    setSearchInput("");

    setPaginationModel({
      page: 0,
      pageSize: DEFAULT_PAGE_SIZE,
    });

    setAppliedFilters({
      device_feature_id: "",
      manufacturer_id: "",
      search: "",
    });
  };


  /* ==========================================================
     REMOVE DEVICE FEATURE FILTER
  ========================================================== */

  const removeFeatureFilter = () => {
    setFeatureFilter("");

    setPaginationModel((previous) => ({
      ...previous,
      page: 0,
    }));

    setAppliedFilters((previous) => ({
      ...previous,
      device_feature_id: "",
    }));
  };


  /* ==========================================================
     REMOVE MANUFACTURER FILTER
  ========================================================== */

  const removeManufacturerFilter = () => {
    setManufacturerFilter("");

    setPaginationModel((previous) => ({
      ...previous,
      page: 0,
    }));

    setAppliedFilters((previous) => ({
      ...previous,
      manufacturer_id: "",
    }));
  };


  /* ==========================================================
     REMOVE SEARCH FILTER
  ========================================================== */

  const removeSearchFilter = () => {
    setSearchInput("");

    setPaginationModel((previous) => ({
      ...previous,
      page: 0,
    }));

    setAppliedFilters((previous) => ({
      ...previous,
      search: "",
    }));
  };


  /* ==========================================================
     REFRESH
  ========================================================== */

  const handleRefresh = async () => {
    await loadFilterOptions();

    await loadDevices();
  };


  /* ==========================================================
     SELECTED FEATURE NAME
  ========================================================== */

  const selectedFeatureName = useMemo(() => {
    return (
      deviceFeatures.find(
        (item) =>
          String(item.id) ===
          String(
            appliedFilters.device_feature_id
          )
      )?.name || ""
    );
  }, [
    deviceFeatures,
    appliedFilters.device_feature_id,
  ]);


  /* ==========================================================
     SELECTED MANUFACTURER NAME
  ========================================================== */

  const selectedManufacturerName =
    useMemo(() => {
      return (
        manufacturers.find(
          (item) =>
            String(item.id) ===
            String(
              appliedFilters.manufacturer_id
            )
        )?.name || ""
      );
    }, [
      manufacturers,
      appliedFilters.manufacturer_id,
    ]);


  /* ==========================================================
     ACTIVE FILTER CHECK
  ========================================================== */

  const hasActiveFilters =
    appliedFilters.device_feature_id !==
      "" ||
    appliedFilters.manufacturer_id !==
      "" ||
    appliedFilters.search !== "";


  /* ==========================================================
     DATA GRID COLUMNS
  ========================================================== */

  const columns = useMemo(
    () => [
      {
        field: "id",
        headerName: "ID",
        width: 80,
      },

      {
        field: "imei",
        headerName: "IMEI",
        minWidth: 180,
        flex: 1,
      },

      {
        field: "iccid_primary",
        headerName: "Primary ICCID",
        minWidth: 190,
        flex: 1,
      },

      {
        field: "device_feature_name",
        headerName: "Device Feature",
        minWidth: 230,
        flex: 1.2,

        renderCell: (params) => {
          if (!params.value) {
            return (
              <Typography
                variant="body2"
                color="text.secondary"
              >
                Not Assigned
              </Typography>
            );
          }

          return (
            <Chip
              label={params.value}
              size="small"
              variant="outlined"
            />
          );
        },
      },

      {
        field: "manufacturer_name",
        headerName: "Manufacturer Name",
        minWidth: 230,
        flex: 1.2,
      },
      {
        field: "owner_name",
        headerName: "Owner Name",
        minWidth: 230,
        flex: 1.2,
      },
      {
        field: "device_model_name",
        headerName: "Device Model Name",
        minWidth: 230,
        flex: 1.2,
      },

      {
        field: "stock_upload_status",
        headerName: "IMEI Updated",
        width: 140,

        renderCell: (params) =>
          params.value ? (
            <Chip
              icon={
                <CheckCircleOutlineIcon />
              }
              label="Completed"
              color="success"
              size="small"
              variant="outlined"
            />
          ) : (
            <Chip
              icon={
                <RadioButtonUncheckedIcon />
              }
              label="Pending"
              color="warning"
              size="small"
              variant="outlined"
            />
          ),
      },

      {
        field: "dispatched",
        headerName: "Dispatched",
        width: 130,

        renderCell: (params) => (
          <Chip
            label={
              params.value
                ? "Yes"
                : "No"
            }
            color={
              params.value
                ? "success"
                : "default"
            }
            size="small"
          />
        ),
      },

      {
        field: "fota_vendor",
        headerName: "FOTA Vendor",
        minWidth: 140,
      },

      {
        field: "fota_status",
        headerName: "FOTA Status",
        minWidth: 140,
      },

      {
        field: "provider_sim_status",
        headerName: "SIM Status",
        minWidth: 160,
      },

      {
        field: "basket_name",
        headerName: "Basket",
        minWidth: 160,
      },
    ],
    []
  );


  /* ==========================================================
     RENDER
  ========================================================== */

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100%",
        p: {
          xs: 1,
          sm: 2,
          md: 3,
        },
      }}
    >
      {/* ======================================================
          PAGE HEADER
      ====================================================== */}

      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          mb: 2,
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 2,

            display: "flex",

            alignItems: "center",

            justifyContent:
              "space-between",

            gap: 2,

            flexWrap: "wrap",
          }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
          >
            <Box
              sx={{
                width: 42,
                height: 42,

                borderRadius: 1.5,

                display: "flex",

                alignItems: "center",

                justifyContent:
                  "center",

                bgcolor:
                  "primary.main",

                color:
                  "primary.contrastText",
              }}
            >
              <DevicesOutlinedIcon />
            </Box>

            <Box>
              <Typography
                variant="h6"
                fontWeight={700}
              >
                Stock Device Feature List
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
              >
                View and filter factory
                stock devices
              </Typography>
            </Box>
          </Stack>


          <Tooltip
            title="Refresh device list"
          >
            <span>
              <IconButton
                onClick={handleRefresh}
                disabled={
                  tableLoading ||
                  optionsLoading
                }
              >
                {optionsLoading ? (
                  <CircularProgress
                    size={21}
                  />
                ) : (
                  <RefreshIcon />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Paper>


      {/* ======================================================
          FILTER PANEL
      ====================================================== */}

      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          mb: 2,
        }}
      >
        {/* FILTER HEADER */}

        <Box
          sx={{
            px: 2.5,
            py: 1.75,

            display: "flex",

            alignItems: "center",

            gap: 1,
          }}
        >
          <FilterAltOutlinedIcon
            color="primary"
            fontSize="small"
          />

          <Typography
            variant="subtitle1"
            fontWeight={700}
          >
            Device Filters
          </Typography>
        </Box>

        <Divider />


        {/* FILTER CONTROLS */}

        <Box sx={{ p: 2.5 }}>
          <Grid
            container
            spacing={2}
            alignItems="center"
          >

            
            {/* SEARCH */}

            <Grid
              item
              xs={12}
              md={4}
            >
              <TextField
                fullWidth
                size="small"
                label="Search Devices"
                placeholder="IMEI, UID, ICCID, basket..."
                value={searchInput}
                onChange={(event) => {
                  setSearchInput(
                    event.target.value
                  );
                }}
                onKeyDown={
                  handleSearchKeyDown
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={
                          handleApplyFilters
                        }
                        disabled={
                          tableLoading
                        }
                      >
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            {/* DEVICE FEATURE */}

            <Grid
              item
              xs={12}
              md={4}
            >
              <TextField
                fullWidth
                select
                size="small"
                label="Device Feature"
                value={featureFilter}
                disabled={
                  optionsLoading
                }
                onChange={(event) => {
                  setFeatureFilter(
                    event.target.value
                  );
                }}
              >
                <MenuItem value="">
                  All Device Features
                </MenuItem>

                {deviceFeatures.map(
                  (feature) => (
                    <MenuItem
                      key={feature.id}
                      value={feature.id}
                    >
                      {feature.name}
                    </MenuItem>
                  )
                )}
              </TextField>
            </Grid>


            {/* MANUFACTURER */}

            <Grid
              item
              xs={12}
              md={4}
            >
              <TextField
                fullWidth
                select
                size="small"
                label="Manufacturer"
                value={
                  manufacturerFilter
                }
                disabled={
                  optionsLoading
                }
                onChange={(event) => {
                  setManufacturerFilter(
                    event.target.value
                  );
                }}
              >
                <MenuItem value="">
                  All Manufacturers
                </MenuItem>

                {manufacturers.map(
                  (manufacturer) => (
                    <MenuItem
                      key={
                        manufacturer.id
                      }
                      value={
                        manufacturer.id
                      }
                    >
                      {manufacturer.name}
                    </MenuItem>
                  )
                )}
              </TextField>
            </Grid>




            {/* ACTION BUTTONS */}

            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent:
                    "flex-end",
                  gap: 1,
                  mt: 0.5,
                }}
              >
                <Button
                  variant="outlined"
                  startIcon={
                    <ClearIcon />
                  }
                  onClick={
                    handleClearFilters
                  }
                >
                  Clear
                </Button>

                <Button
                  variant="contained"
                  startIcon={
                    <SearchIcon />
                  }
                  onClick={
                    handleApplyFilters
                  }
                  disabled={
                    tableLoading
                  }
                >
                  Apply Filters
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>


      {/* ======================================================
          ACTIVE FILTERS
      ====================================================== */}

      {hasActiveFilters && (
        <Paper
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            mb: 2,
            px: 2,
            py: 1.25,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            flexWrap="wrap"
          >
            <Typography
              variant="body2"
              fontWeight={600}
              color="text.secondary"
            >
              Active Filters:
            </Typography>


            {/* FEATURE */}

            {selectedFeatureName && (
              <Chip
                size="small"
                icon={
                  <DevicesOutlinedIcon />
                }
                label={`Feature: ${selectedFeatureName}`}
                onDelete={
                  removeFeatureFilter
                }
              />
            )}


            {/* MANUFACTURER */}

            {selectedManufacturerName && (
              <Chip
                size="small"
                icon={
                  <BusinessOutlinedIcon />
                }
                label={`Manufacturer: ${selectedManufacturerName}`}
                onDelete={
                  removeManufacturerFilter
                }
              />
            )}


            {/* SEARCH */}

            {appliedFilters.search && (
              <Chip
                size="small"
                icon={
                  <SearchIcon />
                }
                label={`Search: ${appliedFilters.search}`}
                onDelete={
                  removeSearchFilter
                }
              />
            )}
          </Stack>
        </Paper>
      )}


      {/* ======================================================
          DEVICE INVENTORY
      ====================================================== */}

      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        {/* RESULTS HEADER */}

        <Box
          sx={{
            px: 2.5,
            py: 1.75,

            display: "flex",

            alignItems: "center",

            justifyContent:
              "space-between",

            gap: 2,

            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography
              variant="subtitle1"
              fontWeight={700}
            >
              Device Inventory
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
            >
              {totalRows.toLocaleString()}{" "}
              device
              {totalRows === 1
                ? ""
                : "s"}{" "}
              found
            </Typography>
          </Box>


          {tableLoading && (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
            >
              <CircularProgress
                size={18}
              />

              <Typography
                variant="caption"
                color="text.secondary"
              >
                Loading devices...
              </Typography>
            </Stack>
          )}
        </Box>

        <Divider />


        {/* DATA GRID */}

        <Box
          sx={{
            width: "100%",
            height: 650,
          }}
        >
          <DataGrid
            rows={rows}
            columns={columns}

            loading={tableLoading}

            pagination

            paginationMode="server"

            rowCount={totalRows}

            paginationModel={
              paginationModel
            }

            onPaginationModelChange={
              setPaginationModel
            }

            pageSizeOptions={
              PAGE_SIZE_OPTIONS
            }

            disableRowSelectionOnClick

            disableColumnMenu={false}

            sx={{
              border: 0,

              "& .MuiDataGrid-columnHeaders":
                {
                  backgroundColor:
                    "background.default",

                  fontWeight: 700,
                },

              "& .MuiDataGrid-cell:focus":
                {
                  outline: "none",
                },

              "& .MuiDataGrid-columnHeader:focus":
                {
                  outline: "none",
                },
            }}

            slots={{
              noRowsOverlay: () => (
                <Box
                  sx={{
                    height: "100%",

                    display: "flex",

                    flexDirection:
                      "column",

                    alignItems:
                      "center",

                    justifyContent:
                      "center",

                    gap: 1,
                  }}
                >
                  <DevicesOutlinedIcon
                    sx={{
                      fontSize: 42,
                      color:
                        "text.disabled",
                    }}
                  />

                  <Typography
                    variant="body1"
                    fontWeight={600}
                    color="text.secondary"
                  >
                    No devices found
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.disabled"
                  >
                    Try changing the
                    filters or search
                    criteria.
                  </Typography>
                </Box>
              ),
            }}
          />
        </Box>
      </Paper>


      {/* ======================================================
          ERROR / SUCCESS SNACKBAR
      ====================================================== */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={closeSnackbar}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          severity={
            snackbar.severity
          }
          variant="filled"
          onClose={closeSnackbar}
          sx={{
            minWidth: 320,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};


export default StockDeviceFeatureList;